// W18 (desktop) / H12 (mobile): vehicle page. Current phase with the move
// actions, last reception (twin + evidence), parts and the full history of
// every stay. On mobile the move actions sit in a bar above the bottom nav.
import { normalizarPatente, type EstadoZona, type Foto } from '@taller/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowRight,
  CircleCheckBig,
  ClipboardList,
  Download,
  Ellipsis,
  Mail,
  MessageCircle,
  Package,
  Pencil,
  Phone,
  Undo2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useAvisos,
  useConfig,
  useInspeccionesPatente,
  useOrdenesAbiertas,
  useOrdenesPatente,
  useUsuarios,
  useVehiculo,
  useVerificacionFotos,
} from '@/datos/consultas';
import {
  aMillis,
  fechaInspeccion,
  type Fase,
  type InspeccionDoc,
  type OrdenDoc,
  type VerificacionFoto,
} from '@/datos/tipos';
import { Galeria } from '@/evidencia/Galeria';
import { descargar } from '@/evidencia/urls';
import { Visor, type ItemVisor } from '@/evidencia/Visor';
import { conteoDanos, Gemelo } from '@/gemelo/Gemelo';
import { nombreTipo } from '@/inspecciones/filtros';
import { permisos, useSesionActiva } from '@/lib/sesion';
import { fechaCorta, fechaHora, useAhora } from '@/lib/tiempo';
import { DialogoRepuestos } from '@/repuestos/DialogoRepuestos';
import { InsigniaRepuesto, repuestoAtrasado } from '@/repuestos/estado';
import { Encabezado } from '@/shell/Encabezado';
import { diasEnteros, entregaEstimada, estaAtrasada, textoDias } from '@/tablero/logica';
import { useOrdenesVisibles } from '@/tablero/movimientos';
import { useAccionesOrden, type AccionesOrden } from '@/tablero/useAccionesOrden';
import { ErrorCarga, Esqueleto, Insignia, Tarjeta, TituloTarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';
import { Menu, MenuContenido, MenuDisparador, MenuItem, MenuSeparador } from '@/ui/menu';
import { Contenedor } from '@/ui/pagina';
import { formatearPatente } from '@/ui/patente';
import { ChipSeveridad } from '@/ui/severidad';
import { Estadia, inspeccionesDeEstadia } from '@/vehiculos/Historial';

export const Route = createFileRoute('/_app/vehiculos/$patente')({
  component: Ficha,
});

function Ficha() {
  const patente = normalizarPatente(Route.useParams().patente);
  const s = useSesionActiva();
  const ahora = useAhora();
  const config = useConfig();
  const fases = useMemo(() => config.data?.fases ?? [], [config.data]);
  const vehiculo = useVehiculo(patente);
  const inspQ = useInspeccionesPatente(patente);
  const ordenesQ = useOrdenesPatente(patente);
  const avisos = useAvisos(patente);
  const usuariosQ = useUsuarios();
  // The open order comes from the board list so optimistic moves show here too.
  const abiertasServidor = useOrdenesAbiertas();
  const abiertas = useOrdenesVisibles(abiertasServidor.data);
  const acciones = useAccionesOrden(abiertas, fases);
  const [repuestos, setRepuestos] = useState<{ abierto: boolean; n: number }>({
    abierto: false,
    n: 0,
  });

  const usuarios = useMemo(
    () => new Map((usuariosQ.data ?? []).map((u) => [u.id, u])),
    [usuariosQ.data],
  );
  const orden = abiertas?.find((o) => o.patente === patente) ?? null;
  const cerradas = useMemo(
    () =>
      (ordenesQ.data ?? [])
        .filter((o) => o.cerrada)
        .sort((a, b) => (aMillis(b.creadaEn) ?? 0) - (aMillis(a.creadaEn) ?? 0)),
    [ordenesQ.data],
  );
  const inspecciones = useMemo(() => inspQ.data ?? [], [inspQ.data]);
  const recepcion =
    (orden && inspecciones.find((i) => i.id === orden.inspeccionId)) ??
    inspecciones.find((i) => i.tipo === 'recepcion' && i.estado === 'firmada') ??
    null;
  const ultimaEntrega =
    orden && recepcion
      ? inspecciones.find((i) => i.tipo !== 'recepcion' && i.recepcionId === recepcion.id)
      : undefined;
  // A siniestro has no order: it shows in the stay of the Ingreso started
  // from it; until then it is "pendiente de ingreso" among the loose ones.
  const enEstadias = new Set(
    (ordenesQ.data ?? []).flatMap((o) => inspeccionesDeEstadia(o, inspecciones).map((i) => i.id)),
  );
  const sueltas = inspecciones.filter((i) => !enEstadias.has(i.id) && i.id !== recepcion?.id);
  // Without an Ingreso yet, the latest closed siniestro is the car's current record.
  const principal =
    recepcion ?? inspecciones.find((i) => i.tipo === 'siniestro' && i.estado === 'firmada') ?? null;

  const datos = principal ?? inspecciones[0];
  const titulo = datos
    ? `${datos.vehiculo.marca} ${datos.vehiculo.modelo}${datos.vehiculo.anio ? ` ${datos.vehiculo.anio}` : ''}`
    : vehiculo.data?.marca
      ? `${vehiculo.data.marca} ${vehiculo.data.modelo ?? ''}`
      : formatearPatente(patente);
  const cargando = inspQ.isPending || ordenesQ.isPending || !config.data || !abiertas;
  const puedeMover = permisos.moverAutos(s.rol);
  const puedeRepuestos = permisos.editarRepuestos(s.rol);

  if (inspQ.isError || ordenesQ.isError) {
    return (
      <>
        <Encabezado titulo={formatearPatente(patente)} volver={{ a: '/tablero' }} />
        <Contenedor>
          <ErrorCarga error={inspQ.error ?? ordenesQ.error} />
        </Contenedor>
      </>
    );
  }

  const sinNada = !cargando && inspecciones.length === 0 && (ordenesQ.data ?? []).length === 0;

  return (
    <>
      <Encabezado
        ayuda="vehiculo"
        titulo={
          <span className="flex min-w-0 items-center gap-3">
            <span className="patente flex-none text-[26px] tracking-[0.04em] lg:text-[30px]">
              {formatearPatente(patente)}
            </span>
            <span className="min-w-0 truncate font-sans text-[15px] font-bold sm:text-[17px]">
              {titulo}
            </span>
          </span>
        }
        subtitulo={
          datos
            ? [
                datos.vehiculo.color,
                datos.cliente.nombre,
                datos.aseguradora ?? datos.seguro?.compania,
                (datos.nroSiniestro ?? datos.seguro?.numeroSiniestro)
                  ? `siniestro ${datos.nroSiniestro ?? datos.seguro?.numeroSiniestro}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        volver={{ a: '/tablero', texto: 'Volver al tablero' }}
        acciones={
          orden && puedeMover ? (
            <AccionesEscritorio orden={orden} fases={fases} acciones={acciones} />
          ) : undefined
        }
      />

      {sinNada ? (
        <Contenedor>
          <Tarjeta>
            <Vacio
              icono={<ClipboardList />}
              titulo={`No hay registros de ${formatearPatente(patente)}`}
              accion={
                <Boton asChild>
                  <Link to="/inspecciones">Ir a inspecciones</Link>
                </Boton>
              }
            >
              Este vehículo no tiene inspecciones en el taller. Su ficha se crea con la primera
              inspección en la tablet.
            </Vacio>
          </Tarjeta>
        </Contenedor>
      ) : (
        <Contenedor className={cn(orden && puedeMover && 'pb-28 lg:pb-6')}>
          {cargando ? (
            <Esqueleto className="h-[118px] rounded-tarjeta" />
          ) : orden ? (
            <FaseActual orden={orden} fases={fases} ahora={ahora} />
          ) : (
            <Tarjeta className="flex items-center gap-3 p-5 text-[14px]">
              <CircleCheckBig className="size-5 text-sin-strong" aria-hidden="true" />
              <span>
                <b>No está en el taller.</b>{' '}
                <span className="text-text-2">
                  {cerradas[0]?.cerradaEn
                    ? `Se entregó el ${fechaCorta(new Date(aMillis(cerradas[0].cerradaEn) ?? 0))}.`
                    : 'Aparece en el tablero con su próximo ingreso.'}
                </span>
              </span>
            </Tarjeta>
          )}

          <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="flex min-w-0 flex-col gap-5">
              {cargando ? (
                <Esqueleto className="h-80 rounded-tarjeta" />
              ) : principal ? (
                <UltimaRecepcion i={principal} entrega={ultimaEntrega} />
              ) : null}
              {orden && (
                <Repuestos
                  orden={orden}
                  ahora={ahora}
                  editable={puedeRepuestos}
                  onEditar={() => setRepuestos((r) => ({ abierto: true, n: r.n + 1 }))}
                />
              )}
              {datos && <Cliente i={datos} />}
            </div>

            <Tarjeta className="h-fit p-5 sm:p-6">
              <TituloTarjeta>Historial</TituloTarjeta>
              <div className="mt-4 flex flex-col gap-6">
                {cargando ? (
                  <Esqueleto className="h-48" />
                ) : (
                  <>
                    {[...(orden ? [orden] : []), ...cerradas].map((o) => (
                      <Estadia
                        key={o.id}
                        orden={o}
                        abierta={!o.cerrada}
                        inspecciones={inspecciones}
                        avisos={avisos.data ?? []}
                        fases={fases}
                        usuarios={usuarios}
                      />
                    ))}
                    {sueltas.length > 0 && <InspeccionesSueltas lista={sueltas} />}
                  </>
                )}
              </div>
            </Tarjeta>
          </div>
        </Contenedor>
      )}

      {orden && puedeMover && <BarraMovil orden={orden} fases={fases} acciones={acciones} />}
      {orden && (
        <DialogoRepuestos
          key={`rep-${repuestos.n}`}
          orden={orden}
          abierto={repuestos.abierto}
          onCambio={(v) => setRepuestos((r) => ({ ...r, abierto: v }))}
        />
      )}
      {acciones.dialogos}
    </>
  );
}

function AccionesEscritorio({
  orden,
  fases,
  acciones,
}: {
  orden: OrdenDoc;
  fases: Fase[];
  acciones: AccionesOrden;
}) {
  const ultima = orden.faseIdx === fases.length - 1;
  const siguiente = fases[orden.faseIdx + 1];
  return (
    <>
      <Boton onClick={() => acciones.pedirAviso(orden)} className="max-lg:hidden">
        <MessageCircle /> Avisar al cliente
      </Boton>
      {!orden.enZonaNeutral &&
        (ultima ? (
          <Boton
            variante="primario"
            onClick={() => acciones.pedirTerminar(orden)}
            className="max-lg:hidden"
          >
            <CircleCheckBig /> Marcar como entregado
          </Boton>
        ) : (
          <Boton
            variante="primario"
            onClick={() => acciones.avanzar(orden)}
            className="max-lg:hidden"
            cargando={acciones.mover.isPending}
          >
            Pasar a {siguiente?.nombre} <ArrowRight />
          </Boton>
        ))}
      <Menu>
        <MenuDisparador asChild>
          <Boton tamano="icono" aria-label="Más acciones">
            <Ellipsis />
          </Boton>
        </MenuDisparador>
        <MenuContenido className="w-64">
          <MenuItem
            icono={<MessageCircle />}
            onSelect={() => acciones.pedirAviso(orden)}
            className="lg:hidden"
          >
            Avisar al cliente…
          </MenuItem>
          {orden.enZonaNeutral && (
            <MenuItem icono={<ArrowRight />} onSelect={() => acciones.pedirForzar(orden)}>
              Forzar entrada a {fases[orden.faseDestino ?? orden.faseIdx]?.nombre}…
            </MenuItem>
          )}
          {orden.faseIdx > 0 && !orden.enZonaNeutral && (
            <MenuItem icono={<Undo2 />} onSelect={() => acciones.pedirVolver(orden)}>
              Volver a una fase anterior…
            </MenuItem>
          )}
          <MenuSeparador />
          <MenuItem icono={<ClipboardList />} asChild>
            <Link to="/tablero" search={{ fase: orden.faseIdx }}>
              Ver en el tablero
            </Link>
          </MenuItem>
        </MenuContenido>
      </Menu>
    </>
  );
}

/** H12 mobile: "Volver fase" / "Pasar a {siguiente} →" above the bottom nav. */
function BarraMovil({
  orden,
  fases,
  acciones,
}: {
  orden: OrdenDoc;
  fases: Fase[];
  acciones: AccionesOrden;
}) {
  const ultima = orden.faseIdx === fases.length - 1;
  const siguiente = fases[orden.faseIdx + 1];
  return (
    <div className="fixed inset-x-0 bottom-[calc(78px+env(safe-area-inset-bottom))] z-30 flex gap-2.5 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur-md lg:hidden">
      {orden.enZonaNeutral ? (
        <Boton
          variante="primario"
          tamano="lg"
          className="flex-1"
          onClick={() => acciones.pedirForzar(orden)}
        >
          Forzar entrada a {fases[orden.faseDestino ?? orden.faseIdx]?.nombre}
        </Boton>
      ) : (
        <>
          <Boton
            tamano="lg"
            className="flex-none"
            disabled={orden.faseIdx === 0}
            onClick={() => acciones.pedirVolver(orden)}
          >
            <Undo2 /> Volver
          </Boton>
          {ultima ? (
            <Boton
              variante="primario"
              tamano="lg"
              className="min-w-0 flex-1"
              onClick={() => acciones.pedirTerminar(orden)}
            >
              Entregar
            </Boton>
          ) : (
            <Boton
              variante="primario"
              tamano="lg"
              className="min-w-0 flex-1"
              onClick={() => acciones.avanzar(orden)}
              cargando={acciones.mover.isPending}
            >
              <span className="truncate">Pasar a {siguiente?.nombre}</span> <ArrowRight />
            </Boton>
          )}
        </>
      )}
    </div>
  );
}

function FaseActual({ orden, fases, ahora }: { orden: OrdenDoc; fases: Fase[]; ahora: number }) {
  const atrasada = estaAtrasada(orden, fases, ahora);
  const dias = diasEnteros(orden, ahora);
  const entrega = entregaEstimada(orden, fases, ahora);
  const destino = orden.faseDestino ?? orden.faseIdx;
  const actual = fases[orden.faseIdx];
  const puesto = `Esperando cupo en ${fases[destino]?.nombre ?? 'su fase'}`;
  return (
    <Tarjeta className={cn('p-5 sm:p-6', atrasada && 'border-atraso-borde')}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-[13px] font-bold tracking-[0.06em] text-text-2 uppercase">Fase actual</p>
        <h2 className="titulo text-[22px] leading-tight">
          {orden.enZonaNeutral ? 'Zona neutral' : actual?.nombre}
        </h2>
        <span
          className={cn('text-[14px]', atrasada ? 'font-bold text-atraso-texto' : 'text-text-2')}
        >
          {orden.enZonaNeutral
            ? puesto
            : atrasada
              ? `▲ ${textoDias(dias)} · atrasado (lo normal: ${actual?.umbralAtrasoDias} días)`
              : dias < 1
                ? 'Entró hoy a esta fase'
                : `${textoDias(dias)} en esta fase`}
        </span>
        {entrega && (
          <span className="text-[14px] text-text-2 sm:ml-auto">
            Entrega estimada <b className="tabular text-text">{fechaCorta(entrega)}</b>
          </span>
        )}
      </div>
      <ol
        className="mt-4 grid gap-1"
        style={{ gridTemplateColumns: `repeat(${fases.length}, minmax(0, 1fr))` }}
        aria-label="Avance por fases"
      >
        {fases.map((f, i) => {
          const hecho = i < orden.faseIdx || (orden.enZonaNeutral && i === orden.faseIdx);
          const aqui = !orden.enZonaNeutral && i === orden.faseIdx;
          const espera = orden.enZonaNeutral && i === destino;
          return (
            <li
              key={f.id}
              className="flex min-w-0 flex-col gap-1.5"
              aria-current={aqui ? 'step' : undefined}
            >
              <span
                className={cn(
                  'h-2 rounded-full',
                  aqui
                    ? atrasada
                      ? 'bg-ray-strong'
                      : 'bg-primary'
                    : hecho
                      ? 'bg-primary/35'
                      : espera
                        ? 'bg-[repeating-linear-gradient(45deg,var(--tm-ray-strong)_0_4px,transparent_4px_8px)]'
                        : 'bg-cupo-pista',
                )}
              />
              <span
                className={cn(
                  'hidden truncate text-[12px] md:block',
                  aqui ? 'font-bold text-text' : 'text-text-2',
                )}
                title={f.nombre}
              >
                {f.nombre}
              </span>
              <span className="sr-only md:hidden">
                {f.nombre}
                {aqui ? ' (actual)' : hecho ? ' (hecha)' : ''}
              </span>
            </li>
          );
        })}
      </ol>
      {orden.retencion && (
        <p className="mt-3 text-[13px] text-text-2">
          Mientras espera repuestos:{' '}
          <b className="text-text">
            {orden.retencion === 'retenido' ? 'queda en el taller' : 'se devolvió al cliente'}
          </b>
        </p>
      )}
    </Tarjeta>
  );
}

function UltimaRecepcion({ i, entrega }: { i: InspeccionDoc; entrega: InspeccionDoc | undefined }) {
  const verifsQ = useVerificacionFotos(i.id);
  const verifs = useMemo(() => new Map((verifsQ.data ?? []).map((v) => [v.id, v])), [verifsQ.data]);
  const [visor, setVisor] = useState<{ items: ItemVisor[]; i: number } | null>(null);
  const [bajando, setBajando] = useState(false);
  const estados = Object.fromEntries(Object.entries(i.zonas ?? {}).map(([k, v]) => [k, v.estado]));
  const conteo = conteoDanos(estados);
  const fotos = Object.values(i.fotos ?? {}).sort((a, b) =>
    a.capturadaEn.localeCompare(b.capturadaEn),
  );
  const fecha = fechaInspeccion(i);
  const abrir = (f: Foto, vs: Map<string, VerificacionFoto>) =>
    setVisor({ items: fotos.map((x) => ({ foto: x, verif: vs.get(x.id) })), i: fotos.indexOf(f) });

  const pdf = async () => {
    if (!i.pdfPath) return;
    setBajando(true);
    try {
      const nombre = i.tipo === 'siniestro' ? 'informe-siniestro' : 'ingreso';
      await descargar(i.pdfPath, `${nombre}-${i.numeroCorrelativo ?? i.id}-${i.patente}.pdf`);
    } catch {
      toast.error('No se pudo descargar el PDF');
    } finally {
      setBajando(false);
    }
  };

  return (
    <Tarjeta className="p-5 sm:p-6">
      <TituloTarjeta
        accion={
          <Link
            to="/inspecciones/$inspeccionId"
            params={{ inspeccionId: i.id }}
            className="text-[13px] font-bold text-primary-text hover:underline"
          >
            Abrir inspección
          </Link>
        }
      >
        {nombreTipo[i.tipo]}
        {i.numeroCorrelativo ? ` N° ${i.numeroCorrelativo}` : ''}
        <span className="tabular ml-2 text-[13px] font-normal text-text-2">
          {fecha ? fechaHora(fecha) : ''}
        </span>
      </TituloTarjeta>
      <div className="mt-4 flex flex-col gap-5 sm:flex-row">
        <div className="flex justify-center sm:w-[150px]">
          <Gemelo
            estados={estados}
            ancho={130}
            etiqueta={`Daños registrados en ${i.tipo === 'siniestro' ? 'el siniestro' : 'el ingreso'}`}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap gap-1.5">
            {(['que', 'gol', 'ray'] as EstadoZona[]).map((e) =>
              conteo[e] ? <ChipSeveridad key={e} estado={e} cantidad={conteo[e]} /> : null,
            )}
            {!conteo.que && !conteo.gol && !conteo.ray && <ChipSeveridad estado="sin" />}
          </div>
          {i.tipo === 'siniestro' && !i.ingresoId && i.estado === 'firmada' && (
            <p className="text-[14px] text-text-2">
              <b className="text-text">Pendiente de ingreso.</b> El ingreso se crea en la tablet
              desde esta inspección cuando el auto llegue.
            </p>
          )}
          {entrega && (
            <Link
              to="/inspecciones/$inspeccionId"
              params={{ inspeccionId: entrega.id }}
              className="text-[14px] font-bold text-primary-text hover:underline"
            >
              {nombreTipo[entrega.tipo]} registrada: ver comparación →
            </Link>
          )}
          {fotos.length > 0 ? (
            <Galeria fotos={fotos} verifs={verifs} onAbrir={(f) => abrir(f, verifs)} />
          ) : (
            <p className="text-[14px] text-text-2">Sin fotos de daños.</p>
          )}
          {i.pdfPath && (
            <Boton tamano="sm" className="self-start" onClick={() => void pdf()} cargando={bajando}>
              <Download /> {i.tipo === 'siniestro' ? 'Ver informe PDF' : 'Ver boleta PDF'}
            </Boton>
          )}
        </div>
      </div>
      <Visor
        items={visor?.items ?? []}
        indice={visor?.i ?? null}
        onIndice={(n) => setVisor((v) => (v ? { ...v, i: n } : v))}
        patente={i.patente}
        onCerrar={() => setVisor(null)}
      />
    </Tarjeta>
  );
}

function Repuestos({
  orden,
  ahora,
  editable,
  onEditar,
}: {
  orden: OrdenDoc;
  ahora: number;
  editable: boolean;
  onEditar: () => void;
}) {
  const lista = orden.repuestos ?? [];
  const faltan = lista.filter((r) => r.estado !== 'recibido').length;
  return (
    <Tarjeta className="p-5 sm:p-6">
      <TituloTarjeta
        accion={
          editable ? (
            <Boton tamano="sm" variante="fantasma" onClick={onEditar}>
              <Pencil /> {lista.length ? 'Editar' : 'Registrar pedido'}
            </Boton>
          ) : undefined
        }
      >
        Repuestos
        {lista.length > 0 && (
          <span className="ml-2 text-[13px] font-normal text-text-2">
            {faltan === 0 ? 'todos disponibles' : `${faltan} por llegar`}
          </span>
        )}
      </TituloTarjeta>
      {lista.length === 0 ? (
        <p className="mt-2 flex items-center gap-2 text-[14px] text-text-2">
          <Package className="size-4" aria-hidden="true" /> No espera repuestos.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {lista.map((r, n) => (
            <li key={n} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block text-[15px] font-bold',
                    repuestoAtrasado(r, ahora) && 'text-atraso-texto',
                  )}
                >
                  {r.nombre}
                </span>
                {r.proveedor && (
                  <span className="block text-[13px] text-text-2">{r.proveedor}</span>
                )}
              </span>
              <InsigniaRepuesto r={r} ahora={ahora} />
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  );
}

function Cliente({ i }: { i: InspeccionDoc }) {
  return (
    <Tarjeta className="p-5 sm:p-6">
      <TituloTarjeta>Cliente</TituloTarjeta>
      <div className="mt-2 flex flex-wrap items-start gap-x-8 gap-y-3">
        <div>
          <p className="text-[16px] font-bold">{i.cliente.nombre}</p>
          <p className="tabular text-[14px] text-text-2">{i.cliente.rut}</p>
        </div>
        <div className="flex flex-col gap-1.5 text-[14px]">
          {i.cliente.telefono && (
            <a
              href={`tel:${i.cliente.telefono.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 text-primary-text hover:underline"
            >
              <Phone className="size-4" /> {i.cliente.telefono}
            </a>
          )}
          {i.cliente.correo && (
            <a
              href={`mailto:${i.cliente.correo}`}
              className="inline-flex items-center gap-2 break-all text-primary-text hover:underline"
            >
              <Mail className="size-4" /> {i.cliente.correo}
            </a>
          )}
        </div>
        {i.aseguradora && (
          <div className="text-[14px]">
            <p className="text-text-2">Aseguradora</p>
            <p className="font-bold">
              {i.aseguradora}
              {i.nroSiniestro && (
                <span className="tabular font-normal text-text-2"> · N° {i.nroSiniestro}</span>
              )}
            </p>
          </div>
        )}
      </div>
    </Tarjeta>
  );
}

function InspeccionesSueltas({ lista }: { lista: InspeccionDoc[] }) {
  return (
    <section>
      <h3 className="mb-2 text-[12.5px] font-bold tracking-[0.06em] text-text-2 uppercase">
        Otras inspecciones
      </h3>
      <ul className="flex flex-col gap-1.5">
        {lista.map((i) => {
          const f = fechaInspeccion(i);
          return (
            <li key={i.id}>
              <Link
                to="/inspecciones/$inspeccionId"
                params={{ inspeccionId: i.id }}
                className="flex items-center gap-2 text-[14px] hover:text-primary-text"
              >
                <span className="flex-1">
                  {nombreTipo[i.tipo]}
                  {i.numeroCorrelativo ? ` N° ${i.numeroCorrelativo}` : ''}
                  <span className="tabular ml-2 text-text-2">{f ? fechaCorta(f) : ''}</span>
                </span>
                <Insignia tono={i.estado === 'anulada' ? 'peligro' : 'neutro'}>
                  {i.estado === 'anulada'
                    ? 'Anulada'
                    : i.estado === 'borrador'
                      ? 'Borrador'
                      : i.tipo === 'siniestro'
                        ? i.ingresoId
                          ? 'Cerrado'
                          : 'Pendiente de ingreso'
                        : 'Firmada'}
                </Insignia>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
